import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { useDispatch } from "react-redux";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation, untranslated } from "@i18n";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import Text from "@CubeComponents/html/Text/Text";
import ReviewsListForEntity from "@Features/reviews/ReviewsListForEntity";
import { changeResourceStatusInReview, getStatusBtnStyle } from "@Features/reviews/helpers";
import { fetchListOfStatusesAvailableRequest } from "@Features/reviews/api/reviews.api";
import { modifyIssuesData } from "@Features/issues/state/issues";
import { fetchIssueById } from "@Features/issues/state/bcfData.actions";
import type { AppDispatch } from "@Reducers/index";
import ChangeFileStatusModal from "@Features/reviews/components/Reviews/ChangeFileStatusModal";
import type { SignatureModalState, SignatureModalData } from "@Features/reviews/components/Reviews/reviews.interfaces";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { ReviewStatusItem } from "@ServerTypes/reviews/reviews.schema";
import type { IssueStatusInReview, IssueReview, IssueItem } from "@ServerTypes/issues/issues.schema";
import { Box } from "@mui/material";

export interface IssueStatusPopoverIssue {
  id: NonNullable<IssueItem["id"]>;
  spaceId: string;
  fileVersionId?: string;
  statusInReview?: IssueStatusInReview;
  /** Reviews the issue belongs to, read directly from the issue instead of a separate fetch. */
  reviews?: IssueReview[];
  /**
   * Whether the current user may change this issue's status (issue-level RESOURCE_COMMENT permission).
   * Gates only the single default-review dropdown; per-review permissions are enforced server-side.
   */
  canEditDefaultStatus?: boolean;
}

interface IssueStatusPopoverProps {
  issue: IssueStatusPopoverIssue;
  /** When false the status pill is shown but not clickable (no permission to change status). */
  canEdit?: boolean;
  /** Grid cells: the pill fills the cell width; elsewhere it hugs its content. */
  fullWidth?: boolean;
  /** Called after a successful change so callers can refresh their own view (e.g. a grid row). */
  onStatusChanged?: (issueId: string, statusInReview?: IssueStatusInReview) => void;
}

/**
 * Self-contained status editor used across the Issues List grid, Issue Cards and the Issue Details
 * panel. It renders the status pill (the trigger) and owns the popover: callers only pass the issue
 * + permission and don't manage anchor state. Single default review -> a plain status dropdown that
 * saves directly. Multiple reviews -> the per-review "Reviews" list (`ReviewsListForEntity`), where a
 * non-default review opens the change-status confirmation popup.
 */
function IssueStatusPopover({
  issue,
  canEdit = false,
  fullWidth = false,
  onStatusChanged,
}: Readonly<IssueStatusPopoverProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [statusOptions, setStatusOptions] = useState<ReviewStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);
  const [confirmModal, setConfirmModal] = useState<SignatureModalState>({ show: false, data: null });

  const open = Boolean(anchorEl);
  const statusInReview = issue.statusInReview;
  const canOpen = Boolean(canEdit && issue.id);

  const resource = {
    issueId: issue.id,
    resourceType: "RESOURCE_TYPE_ISSUE" as const,
    fileVersionId: issue.fileVersionId,
  };

  // Read the issue's reviews from the embedded field instead of a separate fetch. A single default
  // review -> plain status dropdown; anything else (multiple / non-default) -> the per-review list.
  const issueReviews = issue.reviews ?? [];
  const soleReview = issueReviews.length === 1 ? issueReviews[0] : undefined;
  const defaultReview = soleReview?.isDefaultReview ? soleReview : undefined;
  const isOnlyDefaultReview = Boolean(defaultReview);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    if (!canOpen) return;
    // Stop the click from reaching the row/card/grid behind the pill.
    event.stopPropagation();
    const target = event.currentTarget;
    setAnchorEl((prev) => (prev ? null : target));
  };

  const handleClose = () => setAnchorEl(null);

  const handleClickAway = (event: Event) => {
    if (anchorEl && event.target instanceof Node && anchorEl.contains(event.target)) return;
    handleClose();
  };

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAnchorEl(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  // A non-default review needs the full confirmation modal. Close the popover first and render the
  // modal at this level (outside the Popover) so the two overlays don't stack and share a lifecycle.
  const handleRequestStatusConfirm = (data: SignatureModalData) => {
    setConfirmModal({ show: true, data });
    handleClose();
  };

  // `closeModal(true)` is only sent on a successful change (the Cancel button forwards its click
  // event), so refetch strictly on `true`. This pulls the authoritative aggregate status (which may
  // become "Varies") into redux for the card/detail, then pushes it to the grid via onStatusChanged.
  const refreshIssueStatus = async () => {
    const fresh = await dispatch(fetchIssueById(issue.id));
    if (fresh?.statusInReview) onStatusChanged?.(issue.id, fresh.statusInReview);
  };

  const handleConfirmModalClose = (didChange?: boolean) => {
    setConfirmModal({ show: false, data: null });
    if (didChange === true) void refreshIssueStatus();
  };

  // Lazy-load the default review's transition-aware statuses only when the dropdown is opened. The
  // grid renders one popover per row, so fetching on mount fired one request per issue on load. Until
  // it opens, `dropdownOptions` shows just the current status, so the closed control needs no request.
  const loadStatusOptions = async () => {
    if (fetchedRef.current || !defaultReview) return;
    fetchedRef.current = true;
    setLoading(true);
    const statusResponse = await fetchListOfStatusesAvailableRequest({
      value: { params: { reviewId: defaultReview.reviewId, resource } },
    } as any);
    if (statusResponse.data?.result) {
      setStatusOptions(statusResponse.data.result);
    }
    setLoading(false);
  };

  // Before the lazy fetch, surface the current status as the sole option so the closed dropdown still
  // renders the right label (and stays reactive to external status changes); afterwards use the list.
  const dropdownOptions = useMemo<ReviewStatusItem[]>(
    () =>
      statusOptions.length ? statusOptions : statusInReview ? [statusInReview as unknown as ReviewStatusItem] : [],
    [statusOptions, statusInReview],
  );

  const statusStyle = statusInReview?.category ? getStatusBtnStyle(statusInReview.category) : undefined;

  const propagateChange = (newStatus?: IssueStatusInReview) => {
    // Guard against a transient/empty reviews fetch blanking the status.
    if (!newStatus?._id) return;
    // Refresh the card/detail (both read the issue from redux); the grid updates via onStatusChanged.
    dispatch(modifyIssuesData({ id: issue.id, statusInReview: newStatus } as any));
    onStatusChanged?.(issue.id, newStatus);
  };

  const handleDefaultStatusChange = async (newValue: any) => {
    if (!newValue?._id || !defaultReview) return;
    const response = await changeResourceStatusInReview({
      statusTo: newValue._id,
      reviewId: defaultReview.reviewId,
      resources: [resource],
    });

    if (response.data.status) {
      dispatch(
        showToastNotification({ content: t("The status has been changed successfully!"), type: TOAST_TYPE_SUCCESS }),
      );
      propagateChange({ _id: newValue._id, name: newValue.name, category: newValue.category });
      handleClose();
    } else {
      dispatch(
        showToastNotification({
          content: response.data.errorMessage ? untranslated(response.data.errorMessage) : SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const renderBody = () => {
    if (loading) {
      return <div className="py-4 text-sm text-text_light">{t("Loading...")}</div>;
    }

    return (
      <ReviewsListForEntity
        resource={resource as any}
        spaceId={issue.spaceId}
        includeDefaultReviews
        confirmForNonDefault
        successMessage={t("The status has been changed successfully!")}
        onRequestStatusConfirm={handleRequestStatusConfirm}
        canEditDefaultStatus={issue.canEditDefaultStatus}
      />
    );
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      {isOnlyDefaultReview ? (
        <AutoComplete
          options={dropdownOptions}
          loading={loading}
          onOpen={loadStatusOptions}
          disabled={!canEdit || !issue.canEditDefaultStatus}
          disableClearable
          valueField="_id"
          getOptionLabel={(option: any) => option?.name || ""}
          value={statusInReview?._id}
          onChange={(_event: any, newValue: any) => handleDefaultStatusChange(newValue)}
          renderOption={(props: any, option: any) => (
            <li {...props} key={option._id}>
              <div className="py-2 w-full">
                <Text style={getStatusBtnStyle(option.category)} className="rounded px-4 py-1 text-sm text-white">
                  {option.name}
                </Text>
              </div>
            </li>
          )}
          sx={{
            width: fullWidth ? "100%" : "8rem",
            minWidth: "8rem",
            ...(statusStyle && {
              "& .MuiInputBase-root": {
                background: `${statusStyle.backgroundColor} !important`,
                borderRadius: "0.25rem",
                fontWeight: 600,
              },
              "& .MuiOutlinedInput-input": {
                background: "transparent !important",
                color: "#fff !important",
                fontSize: "0.875rem",
                fontWeight: 600,
              },
              "& .MuiSvgIcon-root": { color: "#fff" },
              "& .MuiOutlinedInput-notchedOutline": { border: "none !important" },
            }),
          }}
          slotProps={{
            listbox: { sx: { padding: 0 } },
            popper: {
              placement: "bottom-start",
              style: { width: "12rem" },
            },
          }}
          renderInput={(params: any) => <TextFieldComponent {...params} name="status" />}
        />
      ) : (
        <>
          <div
            className={`flex items-center justify-between h-8 gap-1 rounded py-1.5 ${fullWidth ? "w-full justify-between px-2.5" : "w-[8rem] px-4"} ${
              statusInReview?._id ? "font-semibold text-white text-sm" : ""
            } ${canOpen ? "cursor-pointer" : ""}`}
            style={statusInReview?._id ? getStatusBtnStyle(statusInReview.category) : undefined}
            onClick={handleOpen}
          >
            <span className={fullWidth ? "truncate" : ""}>{statusInReview?.name ?? "-"}</span>
            {canOpen && (
              <ArrowDropDownIcon
                fontSize="small"
                sx={{ transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}
              />
            )}
          </div>
          <Popper open={open} anchorEl={anchorEl} placement="bottom-start" sx={{ zIndex: 1300 }}>
            <ClickAwayListener onClickAway={handleClickAway}>
              <Paper
                sx={{
                  borderRadius: "0.5rem",
                  border: "1px solid #dce8f5",
                  boxShadow: "0.25rem 0.25rem 0.9375rem 0 rgba(65, 92, 171, 0.15)",
                }}
              >
                <div className="flex w-[22rem] flex-col gap-4 p-6" onClick={(event) => event.stopPropagation()}>
                  <Text className="text-base font-bold text-[var(--primary-text)]">{t("Reviews")}</Text>
                  {renderBody()}
                </div>
              </Paper>
            </ClickAwayListener>
          </Popper>
          {confirmModal.show && confirmModal.data && (
            <ChangeFileStatusModal
              {...confirmModal.data}
              closeModal={handleConfirmModalClose}
              successMessage={t("The status has been changed successfully!")}
            />
          )}
        </>
      )}
    </Box>
  );
}

export default IssueStatusPopover;
